// client/src/components/admin/UserVersionFilesManager.js
import { Delete as DeleteIcon, Save as SaveIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { userVersionFileService } from "../../services/userVersionFileService";

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
};

// Admin dialog to register/replace/unregister personal zips.
// mode="version": rows are license-server users of versionId's product.
// mode="user": rows are versions of productId for userId.
const UserVersionFilesManager = ({
  open,
  onClose,
  mode,
  versionId,
  versionLabel,
  userId,
  userEmail,
  productId,
  productName,
  onChanged,
}) => {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      if (mode === "version") {
        const data = await userVersionFileService.getVersionStatus(versionId);
        setRows(
          data.users.map((u) => ({
            key: u.id,
            targetUserId: u.id,
            targetVersionId: versionId,
            label: u.email,
            file: u.file,
          })),
        );
      } else {
        const data = await userVersionFileService.getUserProductStatus(
          userId,
          productId,
        );
        setRows(
          data.versions.map((v) => ({
            key: v.id,
            targetUserId: userId,
            targetVersionId: v.id,
            label: `v${v.version}${v.is_latest ? " (latest)" : ""}`,
            file: v.file,
          })),
        );
      }
      setDrafts({});
    } catch (err) {
      setError(err.message || "Failed to load zip status");
    } finally {
      setLoading(false);
    }
  }, [mode, versionId, userId, productId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleRegister = async (row) => {
    const filePath = (drafts[row.key] || "").trim();
    if (!filePath) return;
    try {
      setSavingKey(row.key);
      setError("");
      await userVersionFileService.registerZip(
        row.targetUserId,
        row.targetVersionId,
        filePath,
      );
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message || "Failed to register zip");
    } finally {
      setSavingKey(null);
    }
  };

  const handleUnregister = async (row) => {
    if (!window.confirm("Unregister this zip? The version will disappear for this user."))
      return;
    try {
      setSavingKey(row.key);
      setError("");
      await userVersionFileService.unregisterZip(
        row.targetUserId,
        row.targetVersionId,
      );
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.message || "Failed to unregister zip");
    } finally {
      setSavingKey(null);
    }
  };

  const title =
    mode === "version"
      ? `License server zips — ${productName || ""} ${versionLabel || ""}`
      : `License server zips — ${userEmail || ""} / ${productName || ""}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Alert severity="info">
            {mode === "version"
              ? "This product has no license server users."
              : "This product has no versions."}
          </Alert>
        ) : (
          <List>
            {rows.map((row) => (
              <ListItem key={row.key} divider sx={{ gap: 2, flexWrap: "wrap" }}>
                <ListItemText
                  primary={row.label}
                  secondary={
                    row.file ? (
                      <Chip
                        size="small"
                        color="success"
                        label={`${row.file.file_path} (${formatSize(row.file.file_size)})`}
                      />
                    ) : (
                      <Chip size="small" label="Not registered" />
                    )
                  }
                />
                <TextField
                  size="small"
                  sx={{ minWidth: 260 }}
                  placeholder="e.g., product.1.2.3.zip"
                  value={drafts[row.key] ?? ""}
                  onChange={(e) =>
                    setDrafts((d) => ({ ...d, [row.key]: e.target.value }))
                  }
                  helperText={`File must exist in storage/products/${row.targetUserId}/`}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  disabled={savingKey === row.key || !(drafts[row.key] || "").trim()}
                  onClick={() => handleRegister(row)}
                >
                  {row.file ? "Replace" : "Register"}
                </Button>
                {row.file && (
                  <IconButton
                    size="small"
                    color="error"
                    disabled={savingKey === row.key}
                    onClick={() => handleUnregister(row)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserVersionFilesManager;
